import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import HomeHighlightsSection from "../components/HomeHighlightsSection";
import HomeHeroSection from "../components/HomeHeroSection";
import HomeMarqueeSection from "../components/HomeMarqueeSection";

export default function HomePage() {
    const [featured, setFeatured] = useState([]);
    const [cats, setCats] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loadCats, setLoadCats] = useState(true);
    const [loadFeat, setLoadFeat] = useState(true);
    const [homeErr, setHomeErr] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        setLoadCats(true);
        setLoadFeat(true);
        setHomeErr("");
        api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
        api
            .get("/categories")
            .then((r) => {
                if (!cancelled) setCats(r.data || []);
            })
            .catch(() => {
                if (!cancelled) setHomeErr("No se pudieron cargar los datos. Comprueba la conexión al servidor.");
            })
            .finally(() => {
                if (!cancelled) setLoadCats(false);
            });
        api
            .get("/products", { params: { featured: true, limit: 8 } })
            .then((r) => {
                if (!cancelled) setFeatured(r.data.items || []);
            })
            .catch(() => {
                if (!cancelled) setHomeErr("No se pudieron cargar los datos. Comprueba la conexión al servidor.");
            })
            .finally(() => {
                if (!cancelled) setLoadFeat(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div data-testid="home-page">
            <HomeHeroSection settings={settings} />
            <HomeMarqueeSection brands={settings?.home_marquee_brands} />

            <HomeHighlightsSection highlights={settings?.home_highlights} />

            {/* CATEGORIES */}
            <section className="max-w-7xl mx-auto px-4 py-16">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <span className="lz-section-label">Departamentos</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight">Categorías</h2>
                    </div>
                    <Link to="/productos" className="hidden md:inline-flex lz-btn lz-btn-outline" data-testid="see-all-cats">Ver todo</Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
                    {loadCats && (
                        <div className="col-span-full text-sm text-[var(--lz-navy)]/60 py-6 text-center border-2 border-dashed border-[var(--lz-navy)]/20">
                            Cargando categorías…
                        </div>
                    )}
                    {!loadCats && cats.length === 0 && (
                        <div className="col-span-full text-sm text-[var(--lz-navy)]/60 py-6 text-center border-2 border-dashed border-[var(--lz-navy)]/20">
                            {homeErr || "No hay categorías para mostrar."}
                        </div>
                    )}
                    {cats.slice(0, 8).map((c) => (
                        <Link
                            key={c.id}
                            to={`/categoria/${c.slug}`}
                            className="group block bg-[var(--lz-navy)] text-white p-6 border-2 border-[var(--lz-navy)] hover:bg-[var(--lz-orange)] hover:text-[var(--lz-navy)] transition-colors"
                            data-testid={`home-cat-${c.slug}`}
                        >
                            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--lz-orange)] group-hover:text-[var(--lz-navy)]">Departamento</div>
                            <div className="font-display text-xl md:text-2xl font-bold uppercase mt-2 leading-tight">{c.name}</div>
                            <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-widest font-bold">
                                Explorar <ArrowRight className="w-3 h-3" />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="max-w-7xl mx-auto px-4 py-16">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <span className="lz-section-label">Top ventas</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight">Destacados</h2>
                    </div>
                    <button onClick={() => navigate("/productos")} className="hidden md:inline-flex lz-btn lz-btn-outline" data-testid="see-all-products">
                        Ver todo
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 stagger">
                    {featured.map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
                {loadFeat && (
                    <div className="text-sm text-[var(--lz-navy)]/60 py-8 text-center border-2 border-dashed border-[var(--lz-navy)]/20">
                        Cargando productos…
                    </div>
                )}
                {!loadFeat && featured.length === 0 && (
                    <div className="text-sm text-[var(--lz-navy)]/60 py-8 text-center border-2 border-dashed border-[var(--lz-navy)]/20">
                        {homeErr || "No hay productos destacados por ahora."}
                    </div>
                )}
            </section>

            {/* CTA banner */}
            <section className="max-w-7xl mx-auto px-4 pb-16">
                <div className="bg-[var(--lz-navy)] text-white p-10 md:p-16 grid md:grid-cols-2 gap-8 items-center border-2 border-[var(--lz-navy)]">
                    <div>
                        <span className="lz-section-label">¿Necesitas ayuda?</span>
                        <h3 className="font-display text-3xl md:text-5xl font-bold uppercase leading-tight">Visítanos en sucursal o cotiza por WhatsApp.</h3>
                    </div>
                    <div className="md:text-right">
                        <Link to="/contacto" className="lz-btn lz-btn-primary" data-testid="cta-shop-pro">Ver Contacto <ArrowRight className="w-4 h-4" /></Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
